import { NgClass, NgTemplateOutlet } from '@angular/common'
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core'
import {
  Component,
  Input,
  ViewContainerRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'

import { ArticlesService } from '../../api/articles-service/articles.service'
import type { EditionDto, MagazineDto } from '../../api/responses'
import { EditionComponentsModule } from '../../features/magazines-and-journals/magazines-and-journals-detail-page/components/edition-components.module'
import { SEMagazineArticlesComponent } from '../../features/magazines-and-journals/se-magazine-articles/se-magazine-articles.component'
import type { AccordionSection, YearChip } from '../../types/types'
import { AuthorGuidelinesComponent } from './tab-contents/author-guidelines/author-guidelines.component'
import { MoreInfoComponent } from './tab-contents/more-info/more-info.component'
import { PricesComponent } from './tab-contents/prices/prices.component'

interface GroupedEdition {
  year: string
  editions: EditionDto[]
}

interface TabDefinition {
  id: string
  title: string
  isEditions?: boolean
  isArticles?: boolean
}

@Component({
  selector: 'dhp-magazine-tabs',
  templateUrl: './magazine-tabs.component.html',
  styleUrl: './magazine-tabs.component.scss',
  imports: [
    NgClass,
    NgTemplateOutlet,
    SEMagazineArticlesComponent,
    EditionComponentsModule,
    MoreInfoComponent,
    PricesComponent,
    AuthorGuidelinesComponent,
    TranslatePipe,
  ],
})
export class MagazineTabsComponent implements OnInit, OnChanges {
  readonly id = input.required<number>()
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() magazine!: MagazineDto
  readonly years = input<YearChip[]>([])
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() yearSelected: YearChip | null = null
  readonly editions = input<EditionDto[]>([])
  readonly loading = input<boolean>(false)
  readonly isEditionOverview = input<boolean>(false)
  readonly isSelectionAndEssence = input<boolean>(false)
  readonly yearSelectedChange = output<YearChip | null>()
  sections: AccordionSection[] = []
  readonly tabs = signal<TabDefinition[]>([])
  activeTabId = ''
  isYearFilterOpen = false
  groupedEditions: GroupedEdition[] = []
  error: string | null = null
  readonly filteredEditions = signal<EditionDto[]>([])
  hasArticles = false
  // Track which accordion sections are expanded (for mobile view only)
  expandedAccordionSections = new Set<string>()
  readonly articleContainer = viewChild.required('articleContainer', {
    read: ViewContainerRef,
  })
  private articlesService = inject(ArticlesService)

  constructor() {
    // Initialize the editions section as expanded by default on mobile
    this.expandedAccordionSections.add('editions')
  }

  onYearChange(year: YearChip): void {
    this.yearSelected = year
    this.yearSelectedChange.emit(year)
    this.filterAndProcessEditions()
  }

  ngOnInit(): void {
    // Only initialize if magazine is available
    if (this.magazine) {
      // We only need to initialize tabs now, not sections
      this.initializeTabs()
      this.filterAndProcessEditions()

      // Set the first tab as active by default
      if (this.tabs().length > 0) {
        this.activeTabId = this.tabs()[0].id
      }

      // For Selection & Essence magazines, check if they have articles and AUTOMATICALLY load them
      if (this.isSelectionAndEssence() && this.magazine.id) {
        // Force 'articles' tab to be active for S&E magazines
        const articlesTab = this.tabs().find(tab => tab.id === 'articles')
        if (articlesTab) {
          this.activeTabId = 'articles'

          // For mobile view, also expand the accordion
          this.expandedAccordionSections.add('articles')
        }

        this.checkForArticles()
      }
    }

    // Auto-load articles for Selection & Essence magazines
    if (this.magazine && this.hasArticles) {
      // Force the Articles tab to be active for S&E magazines
      if (this.isSelectionEssenceMagazine()) {
        this.forceArticlesTabActive()
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize when magazine changes
    if (changes['magazine']) {
      // We only need to initialize tabs now, not sections
      this.initializeTabs()
    }

    if (changes['editions'] || changes['yearSelected']) {
      this.filterAndProcessEditions()
    }

    // Set the first tab as active if we have tabs but no active tab
    if (this.tabs().length > 0 && !this.activeTabId) {
      this.activeTabId = this.tabs()[0].id
    }
  }

  processEditions(): void {
    // Group editions by year (still useful for other UI elements)
    const editionsByYear = new Map<string, EditionDto[]>()

    this.filteredEditions().forEach(edition => {
      const year = edition.year?.toString() ?? 'Unknown'
      if (!editionsByYear.has(year)) {
        editionsByYear.set(year, [])
      }
      editionsByYear.get(year)?.push(edition)
    })

    // Convert map to array
    this.groupedEditions = Array.from(editionsByYear.entries()).map(
      ([year, editions]) => ({
        year,
        editions: editions.sort((a, b) => {
          // Sort by edition_version in descending order
          const versionA = parseInt(a.edition_version ?? '0', 10)
          const versionB = parseInt(b.edition_version ?? '0', 10)
          return versionB - versionA
        }),
      })
    )

    // Sort by year descending
    this.groupedEditions.sort((a, b) => b.year.localeCompare(a.year))
  }

  initializeTabs(): void {
    // Create initial tabs and customize based on magazine type
    const initialTabs = this.createInitialTabs()

    // Filter out tabs with no content
    this.tabs.set(this.filterTabsWithContent(initialTabs))

    // Set the active tab
    this.setDefaultActiveTab()

    // Initialize filtered editions
    this.filterAndProcessEditions()
  }

  selectTab(tabId: string): void {
    this.activeTabId = tabId
    // For mobile view, also expand the accordion
    this.expandedAccordionSections.add(tabId)
  }

  isActive(tabId: string): boolean {
    const isActiveTab = this.activeTabId === tabId
    return isActiveTab
  }

  findSectionByComponentName(
    componentName: string
  ): AccordionSection | undefined {
    return this.sections.find(
      section => section.componentName === componentName
    )
  }

  onTabSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement
    if (select.value) {
      this.selectTab(select.value)
    }
  }

  /**
   * Update filteredEditions when the year filter changes from the dialog This
   * method is called by the yearSelectedChange event from the dialog
   */
  updateYearFilter(): void {
    // Use the current yearSelected value to filter editions
    this.filterAndProcessEditions()
  }

  /** Creates a section for the accordion based on a tab */
  createSectionFromTab(tab: TabDefinition): AccordionSection {
    let componentName: AccordionSection['componentName']

    switch (tab.id) {
      case 'more-info':
        componentName = 'more-info'
        break
      case 'editions':
        componentName = 'editions'
        break
      case 'articles':
        componentName = 'articles' // Handle the articles tab
        break
      case 'for-whom':
        componentName = 'for-whom'
        break
      case 'editorial-secretariat':
        componentName = 'editorial-secretariat'
        break
      case 'author-guidelines':
        componentName = 'author-guidelines'
        break
      case 'prices':
        componentName = 'prices'
        break
      default:
        // Default to more-info for other tabs
        componentName = 'more-info'
    }

    return {
      title: tab.title,
      componentName: componentName,
      expanded: this.isActive(tab.id),
    }
  }

  /** Toggle an accordion section open/closed (mobile only) */
  toggleAccordionSection(tabId: string): void {
    if (this.expandedAccordionSections.has(tabId)) {
      this.expandedAccordionSections.delete(tabId)
    } else {
      this.expandedAccordionSections.add(tabId)
    }
  }

  /** Check if an accordion section is expanded (mobile only) */
  isAccordionSectionExpanded(tabId: string): boolean {
    return this.expandedAccordionSections.has(tabId)
  }

  /**
   * Creates a map of language codes to instruction URLs, filtering out
   * undefined values This approach allows for easy addition of more languages
   * in the future
   */
  getAuthorInstructionsUrls(): Record<string, string> {
    const urls: Record<string, string> = {}

    if (this.magazine.author_instructions_nl_url) {
      urls['nl'] = this.magazine.author_instructions_nl_url
    }

    if (this.magazine.author_instructions_en_url) {
      urls['en'] = this.magazine.author_instructions_en_url
    }

    // Additional languages can be added here in the future
    // if (this.magazine.author_instructions_fr_url) {
    //   urls['fr'] = this.magazine.author_instructions_fr_url;
    // }

    return urls
  }

  /** For S&E magazines, check if articles exist */
  private checkForArticles(): void {
    if (!this.magazine?.id) return

    this.articlesService
      .getArticlesByMagazineId(this.magazine.id, 1, 1, '', null, null, null, {
        property: 'edition_order',
      })
      .subscribe({
        next: result => {
          this.hasArticles = result.articles.length > 0
        },
        error: () => {
          this.hasArticles = false
        },
      })
  }

  private filterAndProcessEditions(): void {
    // Ensure we have a default year selected
    const years = this.years()
    if (!this.yearSelected && years && years.length > 0) {
      this.yearSelected = years[0]
      this.yearSelectedChange.emit(this.yearSelected)
    }

    // Set filteredEditions to all editions when no yearSelected is available
    // This ensures content displays even when navigating back
    if (!this.yearSelected || years.length === 0) {
      this.filteredEditions.set([...this.editions()])
      this.processEditions()
      return
    }

    // Filter editions based on selected year
    const filtered = this.editions().filter(edition => {
      if (edition.year === null || edition.year === undefined) {
        return false
      }
      return edition.year === this.yearSelected?.value
    })

    // If no editions match the selected year, show all editions
    this.filteredEditions.set(
      filtered.length === 0 ? [...this.editions()] : filtered
    )

    // Process the filtered editions
    this.processEditions()
  }

  private hasSectionContent(
    componentName: AccordionSection['componentName']
  ): boolean {
    if (!this.magazine) {
      return false
    }

    switch (componentName) {
      case 'more-info':
        return !!(this.magazine.preface ?? this.magazine.subject)
      case 'for-whom':
        return !!(
          this.magazine.for_whom ||
          (this.magazine.tags && this.magazine.tags.length > 0)
        )
      case 'prices':
        return !!this.magazine.pricing
      case 'author-guidelines':
        return !!(
          this.magazine.author_instructions_nl_url ||
          this.magazine.author_instructions_en_url
        )
      default:
        return false
    }
  }

  /** Sets the default active tab */
  private setDefaultActiveTab(): void {
    // For Selection & Essence, always prioritize the Articles tab
    if (this.isSelectionAndEssence()) {
      const articlesTab = this.tabs().find(tab => tab.id === 'articles')
      if (articlesTab) {
        this.activeTabId = articlesTab.id
        // For mobile view, also expand the accordion
        this.expandedAccordionSections.add('articles')
        return
      }
    }

    // Set the editions tab as active by default if it exists and we're not in edition overview
    const editionsTab = this.tabs().find(tab => tab.isEditions)
    if (editionsTab && !this.isEditionOverview()) {
      this.activeTabId = editionsTab.id
    } else if (this.tabs().length > 0) {
      // Otherwise, set the first available tab as active
      this.activeTabId = this.tabs()[0].id
    }
  }

  /** Creates the initial set of tabs based on magazine type */
  private createInitialTabs(): TabDefinition[] {
    // Create all tabs in the correct order
    const initialTabs: TabDefinition[] = []

    // Add editions tab for non-S&E magazines
    const isSelectionAndEssence = this.isSelectionAndEssence()
    if (!isSelectionAndEssence) {
      initialTabs.push({
        id: 'editions',
        title: 'components.magazineTabs.editions',
        isEditions: true,
      })
    }

    // Add other tabs
    initialTabs.push(
      {
        id: 'more-info',
        title: 'components.magazineTabs.aboutJournal',
      },
      {
        id: 'for-whom',
        title: 'components.magazineTabs.forWhom',
      },
      {
        id: 'prices',
        title: 'components.magazineTabs.prices',
      }
    )

    // Add author guidelines tab
    initialTabs.push({
      id: 'author-guidelines',
      title: 'components.magazineTabs.authorGuidelines',
    })

    // Add Articles tab only for Selection and Essence magazines
    if (isSelectionAndEssence) {
      // Add articles tab at the BEGINNING of the tabs
      initialTabs.unshift({
        id: 'articles',
        title: 'components.magazineTabs.articles',
        isArticles: true,
      })
    }

    return initialTabs
  }

  /** Filters tabs based on content availability */
  private filterTabsWithContent(tabs: TabDefinition[]): TabDefinition[] {
    const filteredTabs = tabs.filter(tab => {
      // Handle editions tab
      if (tab.isEditions) {
        // Show editions tab for non-S&E magazines and when not in edition overview
        return !this.isSelectionAndEssence() && !this.isEditionOverview()
      }

      // Handle articles tab - always show for S&E magazines
      if (tab.isArticles) {
        return this.isSelectionAndEssence()
      }

      // Check content for other tabs
      switch (tab.id) {
        case 'more-info':
          return this.hasSectionContent('more-info')
        case 'for-whom':
          return this.hasSectionContent('for-whom')
        case 'prices':
          return this.hasSectionContent('prices')
        case 'editorial-secretariat':
          return true
        case 'author-guidelines':
          return this.hasSectionContent('author-guidelines')
        default:
          return true
      }
    })

    return filteredTabs
  }

  /** Helper method to check if current magazine is Selection and Essence */
  private isSelectionEssenceMagazine(): boolean {
    return this.isSelectionAndEssence()
  }

  /** Helper method to force the articles tab to be active */
  private forceArticlesTabActive(): void {
    const articlesTab = this.tabs().find(tab => tab.id === 'articles')
    if (articlesTab) {
      this.selectTab('articles')

      // For mobile view, also expand the accordion
      this.expandedAccordionSections.add('articles')
    }
  }
}
